import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { MdFilterList, MdAddCircle, MdEdit, MdDelete } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import { Tooltip } from "@mui/material";
import loading from "../../../assets/images/common/loading.gif";
import AppDataGrid from "../../../components/common/AppDataGrid";

function GSTMaster() {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const handleCloseFilterModal = () => setShowFilterModal(false);
  const handleShowFilterModal = () => setShowFilterModal(true);

  const [gstData, setGstData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge"];
  const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());

  const [codeTypes, setCodeTypes] = useState([]);
  const [scopes, setScopes] = useState([]);

  const fetchGstData = async () => {
    try {
      const response = await axios.post(baseUrl + "/gst-master/", {
        action: "READ",
        school_id: userObj.school_id,
      });
      setGstData(response.data || []);
    } catch (error) {
      console.error("Error fetching GST data:", error);
      toast.error("Failed to fetch data");
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchGstData().finally(() => setIsLoading(false));
  }, []);

  const fetchCodeTypes = async () => {
    try {
      const resp = await axios.post(baseUrl + "/GetCodeType/", {
        action: "READ",
        school_id: userObj.school_id,
      });
      setCodeTypes(resp.data || []);
    } catch (e) {
      console.error("Error fetching code types:", e);
      toast.error("Failed to load code types");
    }
  };

  const fetchScopes = async () => {
    try {
      const resp = await axios.get(baseUrl + "/gst-scope/", {
        action: "READ",
        school_id: userObj.school_id,
      });
      setScopes(resp.data || []);
    } catch (e) {
      console.error("Error fetching scopes:", e);
      toast.error("Failed to load scopes");
    }
  };

  useEffect(() => {
    fetchCodeTypes();
    fetchScopes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditClick = (gst_master_id) => {
    const record = gstData.find((r) => r.gst_master_id === gst_master_id);
    if (record) {
      navigate("/finance/gste/add", { state: { GstData: record } });
    }
  };

  const handleDeleteClick = async (gst_master_id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to change the status?"
    );
    if (!confirmDelete) return;

    try {
      const response = await axios.post(baseUrl + "/deletegstmaster/", {
        gst_master_id,
        modified_by: "admin",
        action: "DELETE",
      });
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      toast.success("Record set to InActive");
      fetchGstData();
    } catch (error) {
      console.error("Error deleting GST:", error);
      toast.error("Failed to delete record");
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "code_type_name",
        headerName: "Type",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={params.value}>
            <span>{params.value}</span>
          </Tooltip>
        ),
      },
      {
        field: "hsn_sac_code",
        headerName: "Code",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={params.value}>
            <span>{params.value}</span>
          </Tooltip>
        ),
      },
      {
        field: "scope_name",
        headerName: "Scope",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={params.value}>
            <span>{params.value}</span>
          </Tooltip>
        ),
      },
      {
        field: "input_tax_credit",
        headerName: "ITC",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={params.value}>
            <span>{params.value}</span>
          </Tooltip>
        ),
      },
      {
        field: "reverse_charge_mechanism",
        headerName: "RCM",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={params.value}>
            <span>{params.value}</span>
          </Tooltip>
        ),
      },
      {
        field: "tds_applicable",
        headerName: "TDC",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={params.value}>
            <span>{params.value}</span>
          </Tooltip>
        ),
      },
      {
        field: "activestatus",
        headerName: "Status",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={params.value}>
            <span>{params.value}</span>
          </Tooltip>
        ),
      },
      ...(canSubmit
        ? [
            {
              field: "actions",
              headerName: "Actions",
              flex: 1,
              headerAlign: "center",
              align: "center",
              sortable: false,
              renderCell: (params) => (
                <div className="tableActions">
                  <Tooltip title="Edit" arrow>
                    <span
                      className="commonActionIcons"
                      onClick={() =>
                        handleEditClick(params.row.gst_master_id)
                      }
                    >
                      <MdEdit />
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <span
                      className="commonActionIcons"
                      onClick={() =>
                        handleDeleteClick(params.row.gst_master_id)
                      }
                    >
                      <MdDelete />
                    </span>
                  </Tooltip>
                </div>
              ),
            },
          ]
        : []),
    ],
    [canSubmit, gstData]
  );

  // ✅ UPDATED: use actual field names present in gstData rows
  const searchableColumns = [
    (r) => r.code_type_name,
    (r) => r.hsn_sac_code,
    (r) => r.scope_name,
    (r) => r.input_tax_credit,
    (r) => r.reverse_charge_mechanism,
    (r) => r.tds_applicable,
    (r) => r.activestatus,
  ];

  // Optional: memoize for performance (not required but safe)
  const filteredRecords = useMemo(
    () =>
      (gstData || []).filter((row) =>
        searchableColumns.some((selector) =>
          String(selector(row) || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        )
      ),
    [gstData, searchQuery]
  );

  // ✅ UPDATED: do not refetch data on every keystroke
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const [form, setForm] = useState({
    gst_type: "",
    gst_code: "",
    scope: "", // important
    itc: "",
    rcm: "",
    tdc: "",
    is_active: "",
    action: "FILTER",
  });

  const handleFilterSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      code_type_id: form.gst_type || 0,
      hsn_sac_master_id: form.gst_code || 0,
      scope_id: form.scope || 0,
      input_tax_credit: form.itc || "",
      reverse_charge_mechanism: form.rcm || "",
      tds_applicable: form.tdc || "",
      is_active:
        form.is_active === "" ? null : form.is_active === "true",
      action: "FILTER",
    };

    try {
      const resp = await axios.post(baseUrl + "/gst-master/", payload);
      setGstData(resp.data || []);
      setShowFilterModal(false);
    } catch (error) {
      toast.error("Failed to fetch filtered data");
    }
  };

  const handleReset = async () => {
    setForm({
      gst_type: "",
      gst_code: "",
      scope: "",
      itc: "",
      rcm: "",
      tdc: "",
      is_active: "",
      action: "FILTER",
    });
    fetchGstData();
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">GST Master</h6>
          </div>

          <div>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              className="searchInput"
              onChange={handleSearchChange}
            />
          </div>

          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}
            >
              <Button
                className="secondaryBtn"
                variant="secondary"
                onClick={handleShowFilterModal}
              >
                <MdFilterList />
              </Button>
            </OverlayTrigger>
            {canSubmit && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-top">Add</Tooltip>}
              >
                <Button
                  className="primaryBtn"
                  variant="primary"
                  onClick={() => navigate("/finance/gste/add")}
                >
                  <MdAddCircle />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </div>
      </div>

      <div className="commonTable height100">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={filteredRecords}
              columns={columns}
              getRowId={(r) => r.gst_master_id}
              showToolbar={false}
              pagination
              pageSizeOptions={[10, 20, 50]}
              density="compact"
              maxHeight={600}
            />
          )}
        </div>
      </div>

      {/* Filter Modal */}
      <Modal
        show={showFilterModal}
        onHide={handleCloseFilterModal}
        className="commonFilterModal"
      >
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Form id="filterForm" onSubmit={handleFilterSubmit}>
            <Row>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="gst_type">
                    <Form.Label>Type</Form.Label>
                    <Form.Select
                      name="gst_type"
                      id="gst_type"
                      value={form.gst_type}
                      onChange={(e) =>
                        setForm({ ...form, gst_type: e.target.value })
                      }
                    >
                      <option value="">Select Type</option>
                      {(codeTypes || []).map((type) => (
                        <option
                          key={type.code_type_id}
                          value={type.code_type_id}
                        >
                          {type.code_type_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="scope">
                    <Form.Label>Scope</Form.Label>
                    <Form.Select
                      name="scope"
                      id="scope"
                      value={form.scope}
                      onChange={(e) =>
                        setForm({ ...form, scope: e.target.value })
                      }
                    >
                      <option value="">Select Scope</option>
                      {(scopes || []).map((scope) => (
                        <option
                          key={scope.scope_id}
                          value={scope.scope_id}
                        >
                          {scope.scope_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group controlId="is_active">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={form.is_active}
                      onChange={(e) =>
                        setForm({ ...form, is_active: e.target.value })
                      }
                    >
                      <option value="">Select Status</option>
                      <option value="true">Active</option>
                      <option value="false">InActive</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <Button
            variant="secondary"
            className="btn-info clearBtn me-2"
            onClick={handleReset}
          >
            Reset
          </Button>
          <div>
            <Button
              variant="secondary"
              className="btn-danger secondaryBtn me-2"
              onClick={handleCloseFilterModal}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="btn-success primaryBtn"
              type="submit"
              form="filterForm"
            >
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default GSTMaster;
